<script setup lang="ts">
  import { onMounted, provide, ref, useTemplateRef } from 'vue'
  import {
    Button,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger
  } from '@gat-ship/vue-components'
  import {
    SupportModalProvider,
    SupportSessionTableHistory,
    SupportSessionTableMy,
    supportSessionKey,
    useSupportContactInfo,
    useSupportOpenHours,
    useSupportSession,
    useSupportSessionPermissions
  } from '@gat-ship/support'
  import { useUser } from '@gat-ship/user'
  import { useFormatting } from '@gat-ship/utils'
  import { IconMail, IconPhone } from '@gat-ship/icons'

  const contact = useSupportContactInfo()

  const user = useUser()

  // The panel owns the granting-side session service and provides it, so
  // its lists and the modals hosted by the SupportModalProvider in its
  // template share the same datasources. This is an end-user surface:
  // the Gatship-side assigned-sessions service is deliberately not
  // consumed here — that lives on the /support/assigned route, backed by
  // the app-root SupportSessionAssignedProvider.
  const supportSessionService = useSupportSession()
  provide(supportSessionKey, supportSessionService)

  const supportModalProviderRef = useTemplateRef('supportModalProviderRef')

  const { canGrantSupportAccess } = useSupportSessionPermissions()

  // The modals context lives in the SupportModalProvider BELOW this panel
  // in the tree, so it can't be injected here — the provider's exposed
  // API is used through the template ref instead.
  function grantSupportAccess() {
    supportModalProviderRef.value?.showCreateSupportSession({})
  }

  const { formatPhoneNumber } = useFormatting()

  const { isSupportOpen, openHoursLabel } = useSupportOpenHours()

  const activeTab = ref('sessions')

  // Embedded outside the /support routes, the panel must fetch the
  // sessions list itself. The history list fetches on its own mount.
  onMounted(async () => {
    await supportSessionService.mySessionsDataSource.fetchData()
  })
</script>

<template>
  <SupportModalProvider ref="supportModalProviderRef">
    <div class="flex flex-col gap-6">
      <ModalSection>
        <ModalSectionHeader class="pt-1">
          <ModalSectionHeaderTitle>
            <div class="flex items-center gap-3">
              <span>Support</span>
            </div>
          </ModalSectionHeaderTitle>
          <ModalSectionHeaderDescription>
            {{
              isSupportOpen
                ? 'You can reach our support team by phone or email.'
                : 'We are currently closed. Please send us an email or contact us during our opening hours.'
            }}
          </ModalSectionHeaderDescription>
        </ModalSectionHeader>
        <div class="flex flex-col gap-0 divide-y">
          <div class="flex items-start justify-between py-3">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium">Gatship Support</span>
              <div class="flex items-center gap-1.5">
                <span class="text-muted-foreground text-xs">
                  {{ openHoursLabel }}
                </span>
              </div>
            </div>
            <div class="flex shrink-0 flex-col items-end gap-3">
              <a
                :href="`mailto:${contact.supportEmail}`"
                class="text-muted-foreground hover:text-link flex items-center gap-1 text-sm hover:underline"
              >
                <IconMail class="size-4" />
                {{ contact.supportEmail }}
              </a>
              <a
                :href="`tel:${contact.phone}`"
                class="text-muted-foreground hover:text-link flex items-center gap-1 text-sm hover:underline"
              >
                <IconPhone class="size-4" />
                {{ formatPhoneNumber(contact.phone) }}
              </a>
              <Button
                v-if="canGrantSupportAccess()"
                variant="outline"
                size="sm"
                @click="grantSupportAccess()"
              >
                Grant Support Access
              </Button>
            </div>
          </div>
          <div class="flex items-start justify-between py-3">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium"
                >Office Location & General Inquiries</span
              >
            </div>
            <div class="flex flex-col gap-3">
              <a
                :href="`mailto:${contact.generalEmail}`"
                class="text-muted-foreground hover:text-link flex items-center gap-1 text-sm hover:underline"
              >
                <IconMail class="size-4" />
                {{ contact.generalEmail }}
              </a>
              <div class="text-muted-foreground text-right text-sm">
                <div>{{ contact.companyName }}</div>
                <div v-for="line in contact.addressLines" :key="line">
                  {{ line }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalSection>

      <ModalSection>
        <Tabs v-model="activeTab">
          <div class="flex flex-col gap-3">
            <ModalSectionHeader>
              <ModalSectionHeaderTitle
                >Support Sessions
              </ModalSectionHeaderTitle>
              <ModalSectionHeaderDescription>
                Sessions where the Gatship support team has been granted access.
              </ModalSectionHeaderDescription>
            </ModalSectionHeader>
            <!-- History is tenant-admin only; without it there is a single
               list, so the tab bar renders only for admins. -->
            <TabsList v-if="user.isTenantAdmin.value">
              <TabsTrigger value="sessions">My sessions</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="sessions">
              <SupportSessionTableMy />
            </TabsContent>
            <TabsContent v-if="user.isTenantAdmin.value" value="history">
              <SupportSessionTableHistory />
            </TabsContent>
          </div>
        </Tabs>
      </ModalSection>
    </div>
  </SupportModalProvider>
</template>
